import React, { useCallback, useRef, useState } from 'react'
import { TValidatorFn } from '@utils/customFormValidators'
import { INVALID } from '@constants'

type FieldsWithValidators = Record<string, { validators?: TValidatorFn[]; [key: string]: any }>

type PrevField = {
  value: any
  isValid: boolean
}

function getControlElements(formEl: HTMLFormElement | null, fieldName: string, isRadio: boolean): HTMLElement[] {
  if (!formEl) return []
  if (isRadio) {
    return Array.from(formEl.querySelectorAll<HTMLElement>(`[name="${fieldName}"]`))
  }
  const el = formEl.querySelector<HTMLElement>(`[name="${fieldName}"]`)
  return el ? [el] : []
}

function elementSupportsCheckValidity(el: HTMLElement): boolean {
  return typeof (el as HTMLInputElement).checkValidity === 'function'
}

function getFieldByName(fields: FieldsWithValidators, fieldName: string) {
  return Object.values(fields).find((f) => f.name === fieldName)
}

export function useFormValidation(fields: FieldsWithValidators, values: Record<string, any>, formRef?: React.RefObject<HTMLFormElement>) {
  const [errors, setErrors] = useState<Record<string, string | null>>({})
  /* TODO: checkValidity clears error message onBlur, if the value is the same as the previous value.
   Possibly a Tenzing error. Remove all previousValuesRef related code if Tenzing fixes it. */
  const previousValuesRef = useRef<Record<string, PrevField>>({})
  const internalFormRef = formRef ?? useRef<HTMLFormElement>(null)

  /** Run all custom validators for a single field — stop at first failure */
  const runCustomValidators = useCallback(
    (fieldName: string, currentValues: Record<string, any>) => {
      const field = getFieldByName(fields, fieldName)
      if (!field) return null
      const value = currentValues[fieldName]
      const validators = field.validators ?? []
      // eslint-disable-next-line no-restricted-syntax
      for (const validate of validators) {
        const err = validate(value, currentValues)
        if (typeof err === 'string' && err.length > 0) {
          return err // stop at first error
        }
      }

      return null
    },
    [fields],
  )

  /** Validate a single field (native + custom) */
  const validateField = useCallback(
    async (fieldName: string, updatedValues?: Record<string, any>): Promise<boolean> => {
      const currentValues = updatedValues ?? values
      let errorMessage: string | null = null

      const currentValue = currentValues[fieldName]
      const prevValue = previousValuesRef.current[fieldName]?.value
      const prevIsValid = previousValuesRef.current[fieldName]?.isValid
      const valueChanged = prevValue !== currentValue
      const hasPreviousValue = prevValue !== undefined
      const field = getFieldByName(fields, fieldName)
      const isRadio = field?.type === 'radio'
      const controls = getControlElements(internalFormRef.current, fieldName, isRadio)
      const primaryControl = controls[0] as HTMLInputElement | undefined
      // Always re-validate radio fields
      if (controls[0] && !valueChanged && hasPreviousValue && !isRadio) {
        return prevIsValid
      }

      // Run native (HTML5) validation only when value changed (Tenzing).
      // Skip when the control is not in the document — ZIN/Stencil teardown can make
      // checkValidity throw (CHP2-14792).
      //
      // Radio groups: multiple hosts (e.g. zin-radio) share the same name; the first match
      // can be invalid while another is selected. Only set errorMessage if every control
      // that supports checkValidity is invalid.
      if (isRadio && controls.length > 0) {
        const connected = controls.filter((el) => el.isConnected && elementSupportsCheckValidity(el))
        if (connected.length > 0) {
          const results = await Promise.all(
            connected.map(async (el) => {
              try {
                const ok = await (el as HTMLInputElement).checkValidity()
                return { ok, message: (el as HTMLInputElement).validationMessage || INVALID }
              } catch {
                return { ok: false, message: INVALID }
              }
            }),
          )
          const anyValid = results.some((r) => r.ok)
          if (!anyValid) {
            const firstInvalid = results.find((r) => !r.ok)
            errorMessage = firstInvalid?.message || INVALID
          }
        }
      } else if (!isRadio && primaryControl?.isConnected && elementSupportsCheckValidity(primaryControl)) {
        const control = primaryControl
        try {
          const isValid = await control.checkValidity()
          if (!isValid) {
            errorMessage = control.validationMessage || INVALID
          }
        } catch {
          errorMessage = INVALID
        }
      }

      // Keep native validation authoritative, except for required-empty tel where
      // product copy intentionally replaces browser default copy.
      const shouldOverrideNativeRequiredTelMessage = Boolean(errorMessage && primaryControl && field?.type === 'tel' && field?.required && primaryControl.validity?.valueMissing)
      if (primaryControl && (!errorMessage || shouldOverrideNativeRequiredTelMessage)) {
        const customError = runCustomValidators(fieldName, currentValues)
        if (customError) {
          errorMessage = customError
        }
      }

      if (controls[0]) {
        previousValuesRef.current[fieldName] = { value: currentValue, isValid: !errorMessage }
      }

      setErrors((prev) => ({
        ...prev,
        [fieldName]: errorMessage,
      }))
      return !errorMessage
    },
    [formRef, runCustomValidators, values],
  )

  /** Validate all fields */
  const validateAll = useCallback(async (): Promise<boolean> => {
    const ids = Object.keys(fields)
    const results: boolean[] = []

    // eslint-disable-next-line no-restricted-syntax
    for (const id of ids) {
      // eslint-disable-next-line no-await-in-loop
      const isValid = await validateField(fields[id].name)
      results.push(isValid)
    }

    return results.every(Boolean)
  }, [fields, validateField])

  const getFirstInvalidField = useCallback(() => {
    const root = internalFormRef.current
    if (!root) return null
    const invalidElements = Array.from(root.querySelectorAll<HTMLElement>(':invalid, .international-phone__input-wrapper--error'))
    const firstInvalid = invalidElements[0]
    if (!firstInvalid) return null
    const focusTarget: HTMLElement | null = firstInvalid.classList.contains('international-phone__input-wrapper--error')
      ? (firstInvalid.querySelector<HTMLInputElement>('.react-international-phone-input') ?? firstInvalid.querySelector<HTMLInputElement>('input'))
      : firstInvalid
    return focusTarget
  }, [formRef])

  const focusFirstInvalidField = useCallback(() => {
    try {
      const firstInvalid = getFirstInvalidField()
      firstInvalid?.focus?.()
    } catch (err) {
      console.warn('Could not focus first invalid field', err)
    }
  }, [formRef])

  return {
    formRef: internalFormRef,
    errors,
    setErrors,
    validateField,
    validateAll,
    focusFirstInvalidField,
  }
}
